import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useResolvedPath } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { Box, Button, Typography } from '@mui/material';
import Loading from './components/Loading/Loading';
import Toolbar from './components/Toolbar/Toolbar';
import Filters from './components/Filters/Filters';
import Table from './components/Table/Table';
import { DEFAULT_FILTERS } from './components/constants/filters';
import prepareFilterParams from './utils/prepareFilterParams';
import {
  useGetReportTasksCountQuery,
  useGetReportTasksQuery,
  useLazyGetReportTasksCountQuery,
  useLazyGetReportTasksQuery,
} from '../../../../entities/reports/reportsResource/redux/reportsResource.api';
import { useGetAppUserResourceQuery } from '../../../../entities/account/appUsersResource/redux/appUsersResource.api';
import ButtonWrapper from './styles/ButtonWrapper';

function ReportTasksTableWidget({ companyId }) {
  const navigate = useNavigate();
  const base = useResolvedPath('..');

  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const params = useMemo(
    () => prepareFilterParams(companyId, filters, rowsPerPage, page),
    [companyId, filters, rowsPerPage, page],
  );

  const {
    data: reportTasks = [],
    isLoading,
    isFetching,
    refetch: refetchTasks,
  } = useGetReportTasksQuery(companyId ? { params } : skipToken);

  const { data: reportTasksTotalCount, refetch: refetchCount } = useGetReportTasksCountQuery(
    companyId ? { params } : skipToken,
  );

  const { data: appUsersData, isLoading: isAppUsersLoading, isFetching: isAppUsersFetching } = useGetAppUserResourceQuery(
    companyId ? { params: { 'companyId.equals': companyId, 'role.in': ['ROLE_COMPANY_ADMIN', 'ROLE_USER'] } } : skipToken,
  );

  const loginToFullName = useMemo(() => {
    const users = appUsersData?.data ?? [];
    const map = {};
    users.forEach((user) => {
      const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ');
      if (user?.user?.login) {
        map[user.user.login] = fullName || user.user.login;
      }
    });
    return map;
  }, [appUsersData]);

  // Всегда перезапрашиваем данные при монтировании
  useEffect(() => {
    if (companyId) {
      refetchTasks();
      refetchCount();
    }
  }, [companyId]);

  const [triggerGetReportTasks] = useLazyGetReportTasksQuery();
  const [triggerGetReportTasksCoune] = useLazyGetReportTasksCountQuery();

  const fetchData = useCallback(() => {
    if (!companyId) return;

    triggerGetReportTasks({ params });
    triggerGetReportTasksCoune({ params });
  }, [companyId, params]);

  const toogleFilters = () => {
    setIsFiltersOpen((prev) => !prev);
  };

  const handleChangePage = (_, value) => {
    setPage(value);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (isLoading || isAppUsersLoading || !companyId) {
    return <Loading />;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ color: 'text.secondary' }}>
        Отправка отчетов по расписанию
      </Typography>
      <Toolbar toogleFilters={toogleFilters} />
      <Filters isOpen={isFiltersOpen} filters={filters} setFilters={setFilters} setPage={setPage} />

      {isFetching || isAppUsersFetching ? (
        <Loading />
      ) : (
        <>
          <Table
            reportTasks={reportTasks}
            loginToFullName={loginToFullName}
            count={reportTasksTotalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            fetchData={fetchData}
          />
          <ButtonWrapper>
            <Button variant="contained" onClick={() => navigate(base.pathname)}>
              Назад
            </Button>
          </ButtonWrapper>
        </>
      )}
    </Box>
  );
}

ReportTasksTableWidget.propTypes = {
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default ReportTasksTableWidget;
